import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ORGANIZATION_USER_ROLES,
  ORGANIZATION_USER_ROLE_LABELS,
  normalizeOrganizationUserRoleForSelect,
} from "../../constants/organizationUserRoles.js";

/**
 * Dialog for editing a user's role, status, and permissions.
 */
export default function EditUserDialog({
  open,
  onClose,
  formData = {},
  onFormDataChange,
  onSave,
  permissionsError = "",
  onPermissionsErrorChange,
}) {
  const handlePermissionsChange = (e) => {
    const value = e.target.value.trim();
    if (!value || value === "") {
      onFormDataChange({ ...formData, permissions: {} });
      onPermissionsErrorChange("");
      return;
    }
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed !== "object" || Array.isArray(parsed)) {
        onPermissionsErrorChange("Permissions must be a JSON object (not an array)");
        return;
      }
      onFormDataChange({ ...formData, permissions: parsed });
      onPermissionsErrorChange("");
    } catch {
      onPermissionsErrorChange("Invalid JSON format. Please enter a valid JSON object.");
    }
  };

  const handlePermissionsBlur = (e) => {
    const value = e.target.value.trim();
    if (!value || value === "") {
      onPermissionsErrorChange("");
      return;
    }
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed !== "object" || Array.isArray(parsed)) {
        onPermissionsErrorChange("Permissions must be a JSON object (not an array)");
      } else {
        onPermissionsErrorChange("");
      }
    } catch {
      onPermissionsErrorChange("Invalid JSON format. Please enter a valid JSON object.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel>Role (role)</InputLabel>
              <Select
                value={normalizeOrganizationUserRoleForSelect(formData.role)}
                onChange={(e) => onFormDataChange({ ...formData, role: e.target.value })}
                label="Role (role)"
              >
                {ORGANIZATION_USER_ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {ORGANIZATION_USER_ROLE_LABELS[r]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel>Status (is_active)</InputLabel>
              <Select
                value={formData.is_active ? "active" : "inactive"}
                onChange={(e) =>
                  onFormDataChange({ ...formData, is_active: e.target.value === "active" })
                }
                label="Status (is_active)"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Permissions (permissions)"
              multiline
              rows={4}
              value={JSON.stringify(formData.permissions || {}, null, 2)}
              onChange={handlePermissionsChange}
              onBlur={handlePermissionsBlur}
              error={!!permissionsError}
              helperText={
                permissionsError ||
                'Enter permissions as JSON object (e.g., {"can_edit": true, "can_delete": false})'
              }
              margin="dense"
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained" disabled={!!permissionsError}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
